import { RuleListStatus } from '@wix/rules-panel-ui';
import { enableMapSet, produce } from 'immer';
import { useAtom } from 'jotai';
import { useCallback, useMemo } from 'react';
import { panelStateAtom } from '../../atoms/session-atoms';
import { TPA_PAGE_ANALYSIS_FAILED_THRESHOLD_PERCENTAGE } from '../../consts';
import { isStaticPage } from '../../rule-mappers/shared/helpers';
import { AnalysisState } from './use-panel-atom-state-types';
enableMapSet();
export const usePanelStateAtom = () => {
  const [panelState, setPanelState] = useAtom(panelStateAtom);
  /**
   * 2) GETTERS
   */
  const getRuleState = useCallback((pageId, ruleId) => {
    const pageState = panelState.groupsDataMap.get(pageId);
    const ruleState = pageState == null ? void 0 : pageState.rulesStateMap.get(ruleId);
    if ((ruleState == null ? void 0 : ruleState.ruleId) === ruleId) {
      return ruleState;
    }
    return undefined;
  }, [panelState.groupsDataMap]);
  const getGroupData = useCallback(pageId => panelState.groupsDataMap.get(pageId), [panelState.groupsDataMap]);
  const getPagesData = useCallback(() => panelState.pagesData, [panelState.pagesData]);

  /**
   * 3) SETTERS (using functional updates)
   */

  const setRuleState = useCallback((pageId, ruleId, data) => {
    setPanelState(prev => produce(prev, draft => {
      const pageState = draft.groupsDataMap.get(pageId);
      if (!pageState) {
        return;
      }
      if (!pageState.rulesStateMap) {
        pageState.rulesStateMap = new Map();
      }
      const currentRuleState = pageState.rulesStateMap.get(ruleId);
      pageState.rulesStateMap.set(ruleId, {
        ruleId,
        state: {
          ...(currentRuleState == null ? void 0 : currentRuleState.state),
          ...data
        }
      });
    }));
  }, [setPanelState]);
  const initializePanelState = useCallback(() => {
    setPanelState(prev => produce(prev, draft => {
      for (const pageId of draft.groupsDataMap.keys()) {
        if (!draft.pagesData.has(pageId)) {
          draft.groupsDataMap.delete(pageId);
        }
      }
      for (const [pageId, {
        itemType,
        title,
        innerRoutes,
        tpaPage
      }] of draft.pagesData.entries()) {
        draft.groupsDataMap.set(pageId, {
          analysisState: AnalysisState.FirstAnalyzing,
          failedPages: 0,
          isTpaPage: tpaPage,
          itemType,
          pageId,
          rulesStateMap: new Map(),
          scannedPages: 0,
          title
        });
        draft.lastScanTimeStamp = Date.now();
        draft.globalSectionComponentsDataMap = new Map();
        if (innerRoutes) {
          for (const route of innerRoutes) {
            draft.groupsDataMap.set(route.id ?? '', {
              analysisState: AnalysisState.FirstAnalyzing,
              failedPages: 0,
              isTpaPage: false,
              itemType,
              pageId: route.id ?? '',
              rulesStateMap: new Map(),
              scannedPages: 0,
              title: route.name ?? ''
            });
          }
        }
      }
    }));
  }, [setPanelState]);
  const setGroupData = useCallback((pageId, data) => {
    setPanelState(prev => produce(prev, draft => {
      const groupData = draft.groupsDataMap.get(pageId);
      if (groupData) {
        draft.groupsDataMap.set(pageId, {
          ...groupData,
          ...data
        });
      }
    }));
  }, [setPanelState]);
  const setGroupsAnalysisState = useCallback((analysisState, itemType, shoulClearScannedPage) => {
    setPanelState(prev => produce(prev, draft => {
      draft.groupsDataMap.forEach(group => {
        if (!itemType || group.itemType === itemType) {
          group.analysisState = analysisState;
          if (shoulClearScannedPage) {
            group.scannedPages = 0;
          }
        }
      });
    }));
  }, [setPanelState]);
  const resetScanPageForAllItemType = useCallback(itemType => {
    setPanelState(prev => produce(prev, draft => {
      for (const groupData of draft.groupsDataMap.values()) {
        if (groupData.itemType === itemType) {
          groupData.scannedPages = 0;
        }
      }
    }));
  }, [setPanelState]);
  const setGroupAnalysisData = useCallback(_ref => {
    let {
      data,
      pageId,
      itemType,
      isJobCompleted = false
    } = _ref;
    setPanelState(prev => produce(prev, draft => {
      var _draft$pagesData$get, _groupData, _groupData2, _groupData3;
      let groupData;
      if (pageId) {
        groupData = draft.groupsDataMap.get(pageId);
      } else if (itemType) {
        groupData = [...draft.groupsDataMap.values()].find(group => group.itemType === itemType && group.isTpaPage);
      }
      const innerRoutes = (_draft$pagesData$get = draft.pagesData.get(((_groupData = groupData) == null ? void 0 : _groupData.pageId) ?? '')) == null ? void 0 : _draft$pagesData$get.innerRoutes;
      const innerRoutesLength = (innerRoutes == null ? void 0 : innerRoutes.length) ?? 0;
      const isFinished = itemType ? ((innerRoutes == null ? void 0 : innerRoutes.length) ?? -1) === ((_groupData2 = groupData) == null ? void 0 : _groupData2.scannedPages) && ((_groupData3 = groupData) == null ? void 0 : _groupData3.analysisState) !== AnalysisState.AnalyzeFailed : true;
      if (groupData) {
        var _data$summary;
        const currentScannedPages = groupData.scannedPages ?? 0;
        groupData.scannedPages = isJobCompleted ? innerRoutesLength : currentScannedPages >= innerRoutesLength ? currentScannedPages : currentScannedPages + 1;
        groupData.analysisData = data;
        if (isFinished || isJobCompleted && groupData.analysisState !== AnalysisState.AnalyzeFailed) {
          groupData.analysisState = AnalysisState.AnalyzeDonePendingResults;
        }
        if (innerRoutes) {
          for (const route of innerRoutes) {
            const innerRouteGroupData = draft.groupsDataMap.get(route.id ?? '');
            if (innerRouteGroupData) {
              innerRouteGroupData.analysisState = AnalysisState.AnalyzeDonePendingResults;
            }
          }
        }
        Object.entries(((_data$summary = data.summary) == null ? void 0 : _data$summary.rulesStatusMap) ?? {}).forEach(_ref2 => {
          var _groupData$rulesState;
          let [ruleId, status] = _ref2;
          const typedRuleId = ruleId;
          // TODO: fix the status here
          const currentStatus = (_groupData$rulesState = groupData.rulesStateMap.get(typedRuleId)) == null || (_groupData$rulesState = _groupData$rulesState.state) == null ? void 0 : _groupData$rulesState.status;
          const newStatus = !isStaticPage(itemType) && currentStatus === RuleListStatus.failed ? RuleListStatus.failed : status === RuleListStatus.failed ? RuleListStatus.failed : status === RuleListStatus.passed ? RuleListStatus.passed : currentStatus ?? undefined;
          groupData.rulesStateMap.set(typedRuleId, {
            ruleId: typedRuleId,
            state: {
              status: newStatus
            }
          });
        });
      }
    }));
  }, [setPanelState]);
  const setGroupExecutionResults = useCallback((pageId, ruleExecutionResults) => {
    setPanelState(prev => produce(prev, draft => {
      const groupData = draft.groupsDataMap.get(pageId);
      if (groupData) {
        groupData.analysisState = AnalysisState.AnalyzeDoneResultsReady;
        groupData.ruleExecutionResults = ruleExecutionResults;
      }
    }));
  }, [setPanelState]);
  const clearModifiedCompIds = useCallback(() => setPanelState(prev => produce(prev, draft => {
    draft.globalSectionComponentsDataMap = new Map();
  })), [setPanelState]);
  const setGlobalSectionData = useCallback(updatedGlobalSectionDataMap => {
    if (updatedGlobalSectionDataMap === undefined) {
      return;
    }
    setPanelState(prev => produce(prev, draft => {
      draft.globalSectionComponentsDataMap = updatedGlobalSectionDataMap;
    }));
  }, [setPanelState]);
  const setPagesData = useCallback(pagesData => {
    setPanelState(prev => produce(prev, draft => {
      draft.pagesData = pagesData;
    }));
  }, [setPanelState]);
  const setPageDataInnerRoute = useCallback((pageId, innerRoutes) => {
    if (!pageId) {
      return;
    }
    setPanelState(prev => produce(prev, draft => {
      draft.pagesData.set(pageId ?? '', {
        ...draft.pagesData.get(pageId ?? ''),
        innerRoutes
      });
    }));
  }, [setPanelState]);
  const setGroupRulesStateMap = useCallback((pageId, newRulesStateMap) => {
    setPanelState(prev => produce(prev, draft => {
      const groupData = draft.groupsDataMap.get(pageId);
      if (groupData) {
        groupData.rulesStateMap.clear();
        newRulesStateMap.forEach((value, key) => {
          groupData.rulesStateMap.set(key, value);
        });
      }
    }));
  }, [setPanelState]);
  const incrementFailedPagesTpa = useCallback(itemType => {
    setPanelState(prev => produce(prev, draft => {
      const tpaGroup = [...draft.groupsDataMap.values()].find(group => group.itemType === itemType && group.isTpaPage);
      if (tpaGroup) {
        var _draft$pagesData$get2;
        tpaGroup.failedPages = (tpaGroup.failedPages ?? 0) + 1;
        const totalNumberOfItems = ((_draft$pagesData$get2 = draft.pagesData.get(tpaGroup.pageId)) == null || (_draft$pagesData$get2 = _draft$pagesData$get2.innerRoutes) == null ? void 0 : _draft$pagesData$get2.length) ?? 0;
        const failedPages = (tpaGroup == null ? void 0 : tpaGroup.failedPages) ?? 0;
        const percentage = failedPages / totalNumberOfItems * 100;
        if (percentage > TPA_PAGE_ANALYSIS_FAILED_THRESHOLD_PERCENTAGE) {
          tpaGroup.analysisState = AnalysisState.AnalyzeFailed;
        }
      }
    }));
  }, [setPanelState]);
  const hasItemInVerticalsDrillInStateUpdate = useCallback(itemType => {
    return panelState.verticalsDrillInStateUpdate.has(itemType);
  }, [panelState.verticalsDrillInStateUpdate]);
  const setItemToVerticalsDrillInStateUpdate = useCallback(itemType => {
    setPanelState(prev => produce(prev, draft => {
      draft.verticalsDrillInStateUpdate.add(itemType);
    }));
  }, [setPanelState]);
  const setEntitiesSummaryAfterCrossPageCalculation = useCallback((pageIdToStateMap, itemType) => {
    setPanelState(prev => produce(prev, draft => {
      draft.groupsDataMap.forEach(group => {
        if (group.itemType === itemType) {
          const stateMap = pageIdToStateMap.get(group.pageId);
          if (stateMap) {
            let issuesCount = 0;
            for (const {
              state
            } of stateMap.values()) {
              if ((state == null ? void 0 : state.status) === 'failed') {
                issuesCount++;
              }
            }
            group.analysisData = {
              summary: {
                issuesCount,
                rulesCount: stateMap.size,
                rulesStatusMap: Object.fromEntries(Array.from(stateMap.entries()).map(_ref3 => {
                  var _value$state;
                  let [ruleId, value] = _ref3;
                  return [ruleId, (_value$state = value.state) == null ? void 0 : _value$state.status];
                }))
              }
            };
            group.rulesStateMap = new Map(Array.from(stateMap.entries()).map(_ref4 => {
              let [key, value] = _ref4;
              return [key, {
                ...value,
                state: value.state ? {
                  ...value.state
                } : undefined
              }];
            }));
          }
        }
      });
    }));
  }, [setPanelState]);
  return useMemo(() => ({
    clearModifiedCompIds,
    // Getters
    getGroupData,
    getPagesData,
    getRuleState,
    hasItemInVerticalsDrillInStateUpdate,
    // Setters
    incrementFailedPagesTpa,
    initializePanelState,
    panelState,
    resetScanPageForAllItemType,
    setEntitiesSummaryAfterCrossPageCalculation,
    setGlobalSectionData,
    setGroupAnalysisData,
    setGroupData,
    setGroupExecutionResults,
    setGroupRulesStateMap,
    setGroupsAnalysisState,
    setItemToVerticalsDrillInStateUpdate,
    setPageDataInnerRoute,
    setPagesData,
    setRuleState
  }), [clearModifiedCompIds, getGroupData, getPagesData, getRuleState, setEntitiesSummaryAfterCrossPageCalculation, hasItemInVerticalsDrillInStateUpdate, incrementFailedPagesTpa, initializePanelState, panelState, resetScanPageForAllItemType, setGlobalSectionData, setGroupAnalysisData, setGroupData, setGroupExecutionResults, setGroupRulesStateMap, setGroupsAnalysisState, setItemToVerticalsDrillInStateUpdate, setPageDataInnerRoute, setPagesData, setRuleState]);
};
//# sourceMappingURL=use-panel-state-atom.js.map